import api from './api'

export const createOperationalLayer = async data => {
  try {
    const response = await api.post('/operational-layers', data)
    return response.data
  } catch (error) {
    console.error('Error creating operational layer:', error)
    throw error
  }
}

const listesEnVol = new Map()

// Une liste partie avant une ecriture porte l ancien updated_at, qui cache-buste les tuiles.
api.interceptors?.response.use(reponse => {
  if (reponse.config?.method !== 'get') listesEnVol.clear()
  return reponse
})

export const getOperationalLayersByResort = (resortId, category = null) => {
  const cle = `${resortId}|${category}`
  if (!listesEnVol.has(cle)) {
    const requete = api
      .get(`/operational-layers/resort/${resortId}`, { params: { category } })
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching operational layers:', error)
        throw error
      })
      .finally(() => {
        if (listesEnVol.get(cle) === requete) listesEnVol.delete(cle)
      })
    listesEnVol.set(cle, requete)
  }
  return listesEnVol.get(cle)
}

export const getOperationalLayerById = async id => {
  try {
    const response = await api.get(`/operational-layers/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching operational layer:', error)
    throw error
  }
}

export const updateOperationalLayer = async (id, data) => {
  try {
    const response = await api.put(`/operational-layers/${id}`, data)
    return response.data
  } catch (error) {
    console.error('Error updating operational layer:', error)
    throw error
  }
}

export const deleteOperationalLayer = async id => {
  try {
    const response = await api.delete(`/operational-layers/${id}`)
    return response.data
  } catch (error) {
    console.error('Error deleting operational layer:', error)
    throw error
  }
}

export const deleteOperationalLayerFeature = async (layerId, featureId) => {
  const response = await api.delete(
    `/operational-layers/${layerId}/features/${featureId}`,
  )
  return response.data
}

export const updateOperationalFeature = async (
  layerId,
  featureId,
  featureData,
) => {
  const response = await api.put(
    `/operational-layers/${layerId}/feature/${featureId}`,
    featureData,
  )
  return response.data
}

// Base du backend = VITE_URL (la MÊME que l'instance axios `api`, qui inclut déjà /api).
// On l'utilise pour le template de tuile afin que les tuiles tapent le bon backend
// (dev: http://localhost:3000/api, prod: l'URL prod) — PAS VITE_API_URL, qui n'est
// pas défini en dev et retomberait sur la prod (→ erreur CORS sur localhost).
// transformRequest (useMapInitialization.js) est ajusté pour créditer cette base.
const TILE_API_BASE = (
  import.meta.env.VITE_URL || 'https://data.eldatechnology.fr/api'
).replace(/\/$/, '')

// Template d'URL de tuile MVT pour une source Mapbox vector.
// Les placeholders {z}/{x}/{y} sont remplacés par Mapbox au moment du fetch.
// version = layer.updated_at — assure le cache-busting côté navigateur.
// VITE_URL inclut déjà /api → pas de préfixe /api ici.
export const operationalLayerTileTemplate = (layerId, version) =>
  `${TILE_API_BASE}/operational-layers/${layerId}/tiles/{z}/{x}/{y}?v=${encodeURIComponent(version || '')}`

export const getLayerFeaturesGeojson = async layerId => {
  const response = await api.get(
    `/operational-layers/${layerId}/features.geojson`,
  )
  return response.data
}

export const getLayerFeatures = async (layerId, params = {}) => {
  const response = await api.get(`/operational-layers/${layerId}/features`, {
    params,
  })
  return response.data
}

export const getLayerFeature = async (layerId, featureId) => {
  const response = await api.get(
    `/operational-layers/${layerId}/features/${featureId}`,
  )
  return response.data
}

export const getPropertyStats = async (layerId, prop) => {
  const response = await api.get(
    `/operational-layers/${layerId}/properties/${encodeURIComponent(prop)}/stats`,
  )
  return response.data
}

// Remplace toutes les features d'une couche (édition pleine-couche, Lot 1d-A).
// Bulk UPSERT keyé UUID côté serveur ; bump updated_at -> cache-bust tuiles au reload.
export const bulkReplaceLayerFeatures = async (layerId, featureCollection) => {
  const response = await api.put(
    `/operational-layers/${layerId}/features`,
    featureCollection,
  )
  return response.data
}

export const updateLayerFeature = async (layerId, featureId, body) => {
  const response = await api.put(
    `/operational-layers/${layerId}/features/${featureId}`,
    body,
  )
  return response.data
}

export const patchLayerSchema = async (layerId, body) => {
  const response = await api.patch(
    `/operational-layers/${layerId}/schema`,
    body,
  )
  return response.data
}

export const searchLayerFeatures = async q => {
  const response = await api.get('/operational-layers/search', {
    params: { q },
  })
  return response.data
}
